import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrevious from 'lib/src/hooks/usePrevious';
import useForm from 'lib/src/hooks/useForm';

import { getVoucherIsPosting, getVoucherPostSuccess, getVouchersError } from '@selectors/vouchers';
import { useHistory } from 'react-router-dom';
import { Voucher } from 'src/types/shared/Voucher';
import dayjs from 'dayjs';
import { updateVoucher } from '@actions/vouchers';
import { removeFieldError } from 'lib/src/redux/actions/fieldErrors';
import { getUtcDateTimeString } from '@utils/dateHelpers';

export default function useUpdateVoucher({
    id,
    title,
    internalName,
    validFrom,
    validTo,
    usesPerIndividual,
    isUnlimited,
    shortDescription,
    description1,
    terms,
    thumbnailS3Key,
    mainImageS3Key,
    stores,
    isHighlighted,
    customBarcode,
    hideVoucherStatusText,
    publishOn,
}: Voucher): UpdateVoucherReturn {
    const dispatch = useDispatch();
    const history = useHistory();

    const closeModal = useCallback(() => {
        history.replace(`/vouchers/${id}`);
    }, [history, id]);

    const [formState, handleChange] = useForm({
        id,
        title,
        internalName,
        validFromDate: new Date(validFrom),
        validFromTime: dayjs(validFrom).format('HH:mm'),
        validToDate: new Date(validTo),
        validToTime: dayjs(validTo).format('HH:mm'),
        usesPerIndividual,
        isUnlimited,
        shortDescription,
        description1: description1 ?? '',
        terms: terms ?? '',
        thumbnailS3Key,
        mainImageS3Key,
        storeIds: stores.map(store => store.id),
        isHighlighted,
        customBarcode,
        hideVoucherStatusText,
        publishOnDate: publishOn ? new Date(publishOn) : null,
        publishOnTime: publishOn ? dayjs(publishOn).format('HH:mm') : '',
    });
    const handleSubmit = useCallback(() => {
        const {
            validFromDate,
            validFromTime,
            validToDate,
            validToTime,
            isUnlimited,
            usesPerIndividual,
            publishOnDate,
            publishOnTime,
        } = formState;
        if (!validFromDate || !validFromTime) return;

        dispatch(
            updateVoucher(id, {
                ...formState,
                usesPerIndividual: isUnlimited ? null : usesPerIndividual,
                validFrom: getUtcDateTimeString(validFromDate, validFromTime) as string,
                validTo: getUtcDateTimeString(validToDate, validToTime) as string,
                publishOn: getUtcDateTimeString(publishOnDate, publishOnTime),
            }),
        );
    }, [dispatch, id, formState]);

    const isPosting = useSelector(getVoucherIsPosting);
    const error = useSelector(getVouchersError);
    const postSuccess = useSelector(getVoucherPostSuccess);
    const prevPostSuccess = usePrevious(postSuccess);

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            closeModal();
        }
    }, [postSuccess, prevPostSuccess, closeModal]);

    useEffect(() => {
        dispatch(removeFieldError('validFrom'));
        dispatch(removeFieldError('validTo'));
    }, [dispatch, formState]);

    return {
        handleSubmit,
        formState,
        handleChange,
        closeModal,
        isPosting,
        error,
    };
}

interface FormState {
    title: string;
    internalName: string;
    validFromDate: Date;
    validFromTime: string;
    validToDate: Date;
    validToTime: string;
    usesPerIndividual: number;
    isUnlimited: boolean;
    shortDescription: string;
    description1: string;
    terms: string;
    thumbnailS3Key: string | null;
    mainImageS3Key: string | null;
    storeIds: number[];
    isHighlighted: boolean;
    customBarcode: string | null;
    hideVoucherStatusText: boolean;
    publishOnDate: Date | null;
    publishOnTime: string;
}

interface UpdateVoucherReturn {
    handleSubmit: () => void;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    closeModal: () => void;
    isPosting: boolean;
    error: string | null;
}
